// @view/panels/workspace/document-creation/DocumentCreation.panel.tsx
import React from "react";
import * as styles from "@styles/DocumentCreation.css";
import type { IOts } from "@ps-bridge/interop";
import type { Ok } from "@ps-bridge/ok";
import EmptyView from "@view/EmptyView";

export type DocumentCreationStepPhase = "details" | "review";
export type DocumentCreationBackPhase = "hidden" | "visible";

export type DocumentCreationStep = {
  id: string;
  title: string;
  caption: string;
};

type DocumentCreationPanelProps = {
  steps: DocumentCreationStep[];
  stepIndex: number;
  stepPhase: DocumentCreationStepPhase;
  backPhase: DocumentCreationBackPhase;
  formView: React.ReactElement;
  reviewView: React.ReactElement;
  nextActionLabel: string;
  nextAction: IOts<Ok>;
  nextDisabled: boolean;
  onBack: IOts<Ok>;
  runIO: (effect: IOts<Ok>) => Ok;
  snapshotView: React.ReactElement;
};

const DocumentCreationPanel: React.FC<DocumentCreationPanelProps> = ({
  steps,
  stepIndex,
  stepPhase,
  backPhase,
  formView,
  reviewView,
  nextActionLabel,
  nextAction,
  nextDisabled,
  onBack,
  runIO,
  snapshotView,
}) => {
  const backView = {
    hidden: <EmptyView />,
    visible: (
      <button
        className={styles.secondaryButton}
        data-testid="document-creation-back"
        onClick={() => runIO(onBack)}
      >
        Back
      </button>
    ),
  } as const satisfies Record<DocumentCreationBackPhase, React.ReactElement>;

  const stepView = {
    details: formView,
    review: reviewView,
  } as const satisfies Record<DocumentCreationStepPhase, React.ReactElement>;

  return (
    <div
      className={styles.studio}
      data-testid="page-documentCreation"
      data-page-state="ready"
    >
      <div className={styles.studioHeader}>
        <div>
          <p className={styles.kicker}>Document Studio</p>
          <h1 className={styles.title}>Create a new document</h1>
          <p className={styles.subtitle}>
            Shape the narrative once. We will guide the structure, tone, and flow
            from your intentions.
          </p>
        </div>
        <ol className={styles.stepper} aria-label="Creation steps">
          {steps.map((step, index) => {
            const state =
              index < stepIndex ? "complete" : index === stepIndex ? "active" : "upcoming";
            return (
              <li key={step.id} className={styles.step} data-state={state}>
                <span className={styles.stepIndex}>
                  {String(index + 1).padStart(2, "0")}
                </span>
                <div>
                  <p className={styles.stepTitle}>{step.title}</p>
                  <p className={styles.stepCaption}>{step.caption}</p>
                </div>
              </li>
            );
          })}
        </ol>
      </div>
      <div className={styles.studioBody}>
        <div className={styles.formCard}>
          {stepView[stepPhase]}
          <div className={styles.actions}>
            {backView[backPhase]}
            <button
              className={styles.primaryButton}
              data-testid="document-creation-next"
              onClick={() => {
                runIO(nextAction);
              }}
              disabled={nextDisabled}
            >
              {nextActionLabel}
            </button>
          </div>
        </div>
        {snapshotView}
      </div>
    </div>
  );
};

export default DocumentCreationPanel;
